<template>
  <div class="visit-type-filter" :class="classModifiers">
    <sp-quantum-select
      class="visit-type-filter__select"
      navigation="accordion"
      selectable-branches
      :branch-toggle-label="branchToggleLabel"
      :title="menuTitle"
      start-on-root
      hide-search-bar
      :items.prop="models"
      :selected="selectedNodeValues"
      :loading="loading"
      :location="location"
      :open="open"
      :pointer="!toBoolean(hidePointer)"
      :readonly="readonly"
      :close-on-select="closeOnSelect"
      :hide-title-bar="hideTitleBar"
      :item-test-id="TestId.finder.visitTypeFilterOption"
      :no-data-text="emptyText"
      @update-selected="handleUpdateSelected"
      @update-open="handleUpdateOpen"
    >
      <FilterActivatorSlot
        :label="label"
        :text="selectedText"
        :prepend-icon="prependIcon"
        :append-icon="appendIcon"
        :open="open"
        @clear="clear"
      >
        <template v-if="$slots.activator" #activator="activatorProps">
          <slot name="activator" v-bind="activatorProps" />
        </template>
      </FilterActivatorSlot>
    </sp-quantum-select>
  </div>
</template>

<script setup>
import { computed, watch } from "vue";
import { watchImmediate } from "@vueuse/core";
import { toBoolean, toJSON, trimToUndefined } from "#ce/utils/props";
import { firstDetail } from "#ce/utils/events";
import { TestId } from "#/test-ids";
import { useForm } from "#ce/composables/useForm";
import { useControllableOpen } from "#ce/apps/shared/filters/composables/useControllableOpen";
import { useVisitTypeFilter } from "#ce/apps/shared/filters/visit-type/composables/useVisitTypeFilter";
import { props as visitTypeFilterProps } from "#ce/apps/shared/filters/visit-type/components/props";
import FilterActivatorSlot from "#ce/apps/shared/filters/components/FilterActivatorSlot.ce.vue";

const emit = defineEmits(["update:model-value", "update:open", "select"]);

const props = defineProps(visitTypeFilterProps);

const { mirrorFormValue } = useForm();

// Parsed once per change: the composable reads it per row while titling the menu.
const categoryLabels = computed(() => toJSON(props.categoryLabels, true) ?? {});

const store = useVisitTypeFilter({
  url: trimToUndefined(props.url),
  modelValue: trimToUndefined(props.modelValue),
  anyLabel: () => props.anyLabel,
  categoryLabels,
});
const { models, loading, error, selectedValue, selectedText, selectedNodeValues, setScope, selectNode, clear } = store;

defineExpose({ props, setScope, selectedText });

const emptyText = computed(() => (error.value ? props.errorText : props.noDataText));

function emitModelValue(value) {
  if (value !== props.modelValue) {
    emit("update:model-value", value);
    emit("select", { value, text: selectedText.value });
  }
}

watchImmediate(selectedValue, emitModelValue);
// The host app dispatches the one change per user update.
mirrorFormValue(selectedValue);

// Keep the selection in sync when the value is set externally (URL param,
// session restore, the in-bar wrapper's clear button).
function syncSelectedFromModelValue(value) {
  const next = trimToUndefined(value);
  if (next !== selectedValue.value) {
    selectedValue.value = next;
  }
}

watch(() => props.modelValue, syncSelectedFromModelValue);

const open = useControllableOpen(() => props.open, emit);

function handleUpdateSelected(event) {
  const [value] = firstDetail(event) ?? [];
  selectNode(value);
}

function handleUpdateOpen(event) {
  open.value = firstDetail(event);
}

const classModifiers = computed(() => ({
  "--inline": toBoolean(props.inline),
  "--has-selected-model": selectedValue.value != null,
}));
</script>

<style>
:host {
  --filter-width: 100%;
  display: block;
  box-sizing: border-box;
}
:host([variant="filter-bar"]) {
  --filter-width: max-content;
  --sp-comp-popup-width: var(--sp-comp-filter-bar-popup-width, 18.5rem);
}
</style>

<style lang="scss" scoped>
sp-quantum-select {
  --sp-comp-list-item-prepend-margin-right: var(--sp-ref-spacing-4, 0.5rem);
  height: 100%;
}

.visit-type-filter {
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 100%;

  &.--inline {
    display: inline-flex;
    --filter-width: auto;
    align-self: center;
  }

  &.--has-selected-model .activator {
    --sp-comp-text-field-color: var(--sp-sys-color-on-surface);
  }
}

sp-quantum-select,
.activator,
.visit-type-filter {
  --sp-comp-button-width: var(--filter-width);
  width: var(--filter-width);
}
</style>
